import { useEffect, useRef } from "react";

/**
 * Turns physical key presses into Arabic characters.
 *
 * Two modes:
 *
 *  • remap (default) — we read `event.code`, the *physical* key position, and
 *    insert the Arabic character ourselves. Position is layout-independent, so
 *    a learner with a plain US keyboard and no Arabic layout installed still
 *    presses exactly the keys a real Arabic keyboard would have them press, and
 *    builds the same muscle memory.
 *
 *  • system — the learner already has an Arabic layout enabled in their OS, so
 *    the browser hands us the Arabic character directly in `event.key` and we
 *    stay out of the way.
 *
 * Listening on window rather than a focused <input> is deliberate: it keeps the
 * IME and autocorrect out of the loop entirely, which matters because combining
 * marks are exactly the thing input methods like to "helpfully" reorder.
 */
export default function useArabicKeyboard({
  layout,
  remap = true,
  enabled = true,
  onChar,
  onBackspace,
}) {
  // Kept in refs so re-rendering on every keystroke doesn't detach the listener.
  const handlers = useRef({ onChar, onBackspace });
  handlers.current = { onChar, onBackspace };

  const config = useRef({ layout, remap, enabled });
  config.current = { layout, remap, enabled };

  useEffect(() => {
    function handleKeyDown(event) {
      const { layout: keys, remap: shouldRemap, enabled: isEnabled } = config.current;
      if (!isEnabled) return;

      // Never swallow browser and OS shortcuts.
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      if (event.key === "Backspace") {
        event.preventDefault();
        handlers.current.onBackspace?.();
        return;
      }

      // Leave Tab, Escape, F-keys and arrows alone so the overlay stays
      // keyboard-navigable and closable.
      if (event.key.length !== 1 && event.key !== "Spacebar") {
        if (!shouldRemap || event.code !== "Space") return;
      }

      if (shouldRemap) {
        const key = keys?.[event.code];
        if (!key) return;
        const char = event.shiftKey ? key.shift : key.base;
        if (!char) return;
        event.preventDefault();
        handlers.current.onChar?.(char, {
          code: event.code,
          shift: !!event.shiftKey,
          repeat: event.repeat,
        });
        return;
      }

      // System-layout mode: trust what the OS produced.
      if (event.key.length === 1) {
        event.preventDefault();
        handlers.current.onChar?.(event.key, {
          code: event.code,
          shift: !!event.shiftKey,
          repeat: event.repeat,
        });
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
}
